import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import { FormDrawer } from '../../../components/ui';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { templateService } from '../services/templateService';
import {
  buildWabaCreateForm,
  buildWabaCreatePayload,
  validateWabaCreateForm,
  WABA_APP_TYPES,
} from '../utils/wabaProfileUtils';

const DRAWER_WIDTH = { xs: '100%', sm: 480, md: 560 };

export default function AddWabaFormDrawer({ open, onClose, onCreated }) {
  const { showSnackBar } = useSnackBar();
  const [form, setForm] = useState(buildWabaCreateForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(buildWabaCreateForm());
      setErrors({});
    }
  }, [open]);

  const isDirty = useMemo(
    () => ['app_name', 'wa_phone_number', 'wa_phone_number_id', 'waba_id', 'app_id', 'user_access_token']
      .some((key) => String(form[key] || '').trim().length > 0),
    [form]
  );

  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose?.();
    }
  }, [isSubmitting, onClose]);

  const handleSubmit = useCallback(async () => {
    const validationErrors = validateWabaCreateForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildWabaCreatePayload(form);
      const res = await templateService.createWabaApp(payload);
      if (res?.status !== 200 || !res?.data) {
        throw new Error(res?.data || 'Unable to create WhatsApp app.');
      }
      showSnackBar('WhatsApp app created successfully.', 'success');
      await onCreated?.(res.data);
      onClose?.();
    } catch (error) {
      const message = error?.response?.data?.data
        || error?.response?.data?.message
        || error?.message
        || 'Unable to create WhatsApp app.';
      showSnackBar(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [form, onClose, onCreated, showSnackBar]);

  return (
    <FormDrawer
      open={open}
      onClose={handleClose}
      title="Add WABA"
      subtitle="Register a WhatsApp Business app directly without Meta OAuth."
      submitLabel="Save WABA"
      cancelLabel="Cancel"
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      confirmBeforeClose={isDirty}
      width={DRAWER_WIDTH}
    >
      <TextField
        fullWidth
        required
        label="Display Name"
        value={form.app_name}
        onChange={(event) => updateField('app_name', event.target.value)}
        error={Boolean(errors.app_name)}
        helperText={errors.app_name}
        margin="normal"
      />
      <TextField
        fullWidth
        required
        label="Phone Number"
        value={form.wa_phone_number}
        onChange={(event) => updateField('wa_phone_number', event.target.value.replace(/\D/g, ''))}
        error={Boolean(errors.wa_phone_number)}
        helperText={errors.wa_phone_number || 'Digits only, no + or spaces'}
        margin="normal"
      />
      <TextField
        fullWidth
        required
        label="Phone Number ID"
        value={form.wa_phone_number_id}
        onChange={(event) => updateField('wa_phone_number_id', event.target.value.replace(/\D/g, ''))}
        error={Boolean(errors.wa_phone_number_id)}
        helperText={errors.wa_phone_number_id}
        margin="normal"
      />
      <TextField
        fullWidth
        required
        label="WABA ID"
        value={form.waba_id}
        onChange={(event) => updateField('waba_id', event.target.value)}
        error={Boolean(errors.waba_id)}
        helperText={errors.waba_id}
        margin="normal"
      />
      <TextField
        fullWidth
        // required
        label="App ID"
        value={form.app_id}
        onChange={(event) => updateField('app_id', event.target.value.replace(/\D/g, ''))}
        // error={Boolean(errors.app_id)}
        // helperText={errors.app_id}
        margin="normal"
      />
      <TextField
        fullWidth
        required
        select
        label="App Type"
        value={form.app_type}
        onChange={(event) => updateField('app_type', event.target.value)}
        error={Boolean(errors.app_type)}
        helperText={errors.app_type}
        margin="normal"
      >
        {WABA_APP_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        label="API Version"
        value={form.wa_version}
        onChange={(event) => updateField('wa_version', event.target.value)}
        margin="normal"
      />
      <TextField
        fullWidth
        label="Messaging Product"
        value={form.wa_messaging_product}
        onChange={(event) => updateField('wa_messaging_product', event.target.value)}
        margin="normal"
      />
      <TextField
        fullWidth
        label="Recipient Type"
        value={form.wa_recipient_type}
        onChange={(event) => updateField('wa_recipient_type', event.target.value)}
        margin="normal"
      />
      <TextField
        fullWidth
        required
        type="password"
        label="Access Token"
        value={form.user_access_token}
        onChange={(event) => updateField('user_access_token', event.target.value)}
        error={Boolean(errors.user_access_token)}
        helperText={errors.user_access_token}
        margin="normal"
      />
      <FormControlLabel
        control={(
          <Switch
            checked={Boolean(form.is_active)}
            onChange={(event) => updateField('is_active', event.target.checked)}
          />
        )}
        label="Live (active)"
      />
    </FormDrawer>
  );
}

AddWabaFormDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
};
